/**
 * Chat history API — GET /v1/chat/history reconciliation.
 *
 * The server's `chat_messages` table is the canonical source of truth
 * for chat exchanges: encrypted at rest via pgcrypto, cross-device,
 * survives client storage clears. The client localStorage cache
 * (`ti:chatHistory`) is a UX accelerator only — instant render on
 * mount, no need to wait for the network before showing the user's
 * recent conversation.
 *
 * On mount, MePage:
 *   1. Hydrates messages from cache → instant render.
 *   2. Fetches GET /v1/chat/history → server canonical.
 *   3. Reconciles: server authoritative for chat exchanges; ephemeral
 *      client-only kinds (onboarding / teatime / memory-offer) stay
 *      in their cached positions. Server-issued ULIDs replace client
 *      temp IDs on first reconciliation — subsequent reloads use
 *      server-canonical IDs end-to-end, enabling clean cross-device
 *      sync.
 *
 * Perfect chronological interleaving with ephemerals would require
 * ISO timestamps on every ChatMessage — deferred (would need a
 * ChatMessage schema bump + persisted v3). For v1 the cache walk
 * preserves the order users actually experience: onboarding first,
 * chat exchanges chronological, teatime/memory-offer at the moments
 * they fired.
 *
 * Identity-scoped TanStack cache key (Batch 2 Part A, 12 Jun 2026):
 *
 *   queryKey: chatHistoryQueryKey(userId) → ['chat', 'history', userId]
 *
 * Mirrors the same structural fix applied to `useUser` / `usePartner`
 * (10 Jun 2026) — account A's chat history is cached under
 * `['chat', 'history', userIdA]` and account B's under
 * `['chat', 'history', userIdB]`. Two different cache entries; no
 * shared key from which a cross-account read could occur. This is
 * a security prerequisite for Batch 2 Part B (persistent cache to
 * IndexedDB) — without it, persisted history from account A would
 * be visible to account B on the next render after sign-in on the
 * same device. The in-memory `queryClient.clear()` on sign-out
 * (SettingsPage, JoinPage) and `clearAllAppStorage` wholesale wipe
 * remain as defence-in-depth, but are no longer load-bearing for
 * cross-account isolation — the key shape itself makes the bleed
 * structurally impossible.
 *
 * `userId` comes from Clerk's `useAuth()` and is the Clerk user id
 * (e.g. `user_2abc...`) — stable for the lifetime of the account,
 * unique across all Clerk accounts in this instance. Coalesced to
 * `null` to keep the key shape stable while Clerk is still resolving
 * (the `enabled` gate below blocks the query firing on the null-key
 * variant, so no stray fetch occurs on the loading-state key).
 *
 * `enabled` is gated on BOTH `isSignedIn` and `userId` — belt and
 * braces against any SDK race condition where `isSignedIn` flips
 * true before `userId` is populated. Caller-supplied `options.enabled`
 * AND's on top of that (MePage passes `!!user?.profile` so
 * mid-onboarding users don't burn a round-trip on a guaranteed-empty
 * response).
 *
 * Invalidation: call sites use `chatHistoryQueryKey(userId)` with the
 * same userId they read with. Partial-prefix invalidation
 * (`['chat', 'history']` without a userId segment) would still match
 * EVERY user's variant by TanStack's default partial-key matching —
 * intentionally avoided to keep cross-account hygiene strict.
 *
 * Pagination order (16 Jun 2026 fix):
 *
 *   The server's GET /v1/chat/history returns messages DESC by ts
 *   (findMessagesPaginated in towninsider-api uses ORDER BY ts DESC
 *   + before-cursor semantics — newest-first is the right shape for
 *   an infinite-scroll cursor walking backward in time). Reconciliation
 *   re-sorts ASC before mapping to client shape, so the merged output
 *   is chronological end-to-end regardless of which direction the
 *   server delivered. Without the re-sort, the append-unconsumed tail
 *   of `reconcileHistory` produced reverse render order on the
 *   sign-out-then-sign-back-in rehydration path, where `current`
 *   starts empty (clearAllAppStorage wiped localStorage on the
 *   outbound sign-out) and every server message flowed through the
 *   tail branch. See the inline comment in `reconcileHistory` for
 *   the full bug-class write-up.
 */
import { useAuth } from '@clerk/clerk-react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import type { ChatMessage } from '../../components/ChatBubble';
import { apiFetch } from '../api';
import type { MessageKind } from '../storage';

// ─── API schemas ─────────────────────────────────────────────────────────

/**
 * One row from the server's `chat_messages` table, decrypted on read.
 * Mirrors the shape returned by `findMessagesPaginated` in
 * `towninsider-api/src/db/chat.ts` (inferred from
 * `tests/integration/chat.integration.test.ts`).
 *
 * `text` is null for rows that don't carry user-visible text (e.g.
 * tool-result placeholders if surfaced via history in future).
 * `kind` is the server-side taxonomy — currently 'text' for chat
 * exchanges; other values are skipped on the mapping path.
 * `pickName` is left in the schema even though new server writes
 * don't populate it (legacy mock-era rows may persist on Postgres
 * for users from before the SSE cutover).
 */
const serverChatMessageSchema = z.object({
  id: z.string().min(1),
  fromWho: z.enum(['user', 'persona', 'system']),
  kind: z.string(),
  text: z.string().nullable(),
  ts: z.string(),
  dateLocal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  pickName: z.string().min(1).nullable().optional(),
});

export type ServerChatMessage = z.infer<typeof serverChatMessageSchema>;

const historyResponseSchema = z.object({
  messages: z.array(serverChatMessageSchema),
  hasMore: z.boolean(),
});

export type HistoryResponse = z.infer<typeof historyResponseSchema>;

/**
 * Server's max limit is 200 (see chat.ts `MAX_HISTORY_LIMIT`). Pulling
 * the full page on every reconciliation is fine — a 200-message
 * conversation is ~30KB compressed, fits well inside a fetch budget.
 * Cursor pagination is wired (`before`) for future infinite-scroll
 * but unused in this batch.
 */
const HISTORY_LIMIT = 200;

// ─── Fetcher ─────────────────────────────────────────────────────────────

interface GetChatHistoryArgs {
  getToken: () => Promise<string | null>;
  limit?: number;
  before?: string;
}

export async function getChatHistory({
  getToken,
  limit = HISTORY_LIMIT,
  before,
}: GetChatHistoryArgs): Promise<HistoryResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (before !== undefined) {
    params.set('before', before);
  }
  return apiFetch(`/v1/chat/history?${params.toString()}`, {
    schema: historyResponseSchema,
    getToken,
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────

export interface UseChatHistoryOptions {
  /**
   * Gate the fetch. Default true. MePage passes `!!user?.profile` so
   * mid-onboarding users don't burn a round-trip on a guaranteed-empty
   * response (the `requireUser` middleware succeeds before onboarding
   * is complete, but no chat rows exist yet).
   */
  enabled?: boolean;
}

/**
 * Identity-scoped query key factory. Call sites pass the Clerk
 * userId they're operating against (typically destructured from
 * `useAuth().userId` in the same scope as the hook caller).
 *
 * The `string | null` parameter accepts Clerk's `useAuth().userId`
 * shape directly — which is `string | null | undefined` during
 * loading — without forcing callers to coerce. `null` keeps the
 * key shape stable across the resolving window; the `enabled`
 * gate on the hook blocks fetches against the null-key variant
 * so no stray request fires.
 */
export function chatHistoryQueryKey(
  userId: string | null,
): readonly ['chat', 'history', string | null] {
  return ['chat', 'history', userId] as const;
}

export function useChatHistory(
  options?: UseChatHistoryOptions,
): UseQueryResult<HistoryResponse> {
  const { isSignedIn, userId, getToken } = useAuth();
  return useQuery<HistoryResponse>({
    queryKey: chatHistoryQueryKey(userId ?? null),
    queryFn: () => getChatHistory({ getToken }),
    enabled: !!isSignedIn && !!userId && (options?.enabled ?? true),
    // Brief stale time — chat is fast-moving, and we invalidate
    // explicitly on chat-exchange completion. Short stale time also
    // means returning to MePage after a few minutes gets a fresh
    // canonical view rather than serving cache indefinitely.
    staleTime: 30 * 1000,
  });
}

// ─── Reconciliation ──────────────────────────────────────────────────────

/**
 * Client message kinds the server stores. After SSE chat shipped, all
 * server-stored exchanges arrive as 'user' or 'persona-reply' on the
 * client side. 'weather' and 'pick-ack' remain in the set for
 * back-compat with persisted history written by the pre-SSE mock —
 * those entries are still valid v2 schema and shouldn't be discarded
 * just because they have a legacy kind.
 *
 * Kinds NOT in this set are client-only (onboarding, teatime,
 * memory-offer, profile-update) and pass through reconciliation
 * untouched in their cached positions.
 */
const SERVER_OWNED_CLIENT_KINDS: ReadonlySet<MessageKind> = new Set([
  'user',
  'persona-reply',
  'weather',
  'pick-ack',
]);

function formatTimeFromIso(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Map a server message to a client ChatMessage shape. Returns null for
 * rows that don't correspond to a renderable bubble:
 *   - `text === null` (placeholder rows, if ever surfaced)
 *   - `kind !== 'text'` (server-side categorisations we don't render)
 *   - `fromWho === 'system'` (out-of-band system events, future use)
 */
function serverMessageToClient(m: ServerChatMessage): ChatMessage | null {
  if (m.text === null) return null;
  if (m.kind !== 'text') return null;
  if (m.fromWho === 'system') return null;

  const from = m.fromWho === 'user' ? 'user' : 'persona';
  const clientKind: MessageKind =
    m.fromWho === 'user' ? 'user' : 'persona-reply';

  const base: ChatMessage = {
    id: m.id,
    from,
    text: m.text,
    timestamp: formatTimeFromIso(m.ts),
    dateLocal: m.dateLocal,
    kind: clientKind,
    status: 'sent',
  };
  // exactOptionalPropertyTypes — only include pickName when it's
  // actually present and non-null. ChatMessage's pickName is optional,
  // not nullable; pass-through of `null` would fail strict TS.
  if (m.pickName !== null && m.pickName !== undefined) {
    return { ...base, pickName: m.pickName };
  }
  return base;
}

/**
 * Reconcile current in-memory messages against the server canonical.
 *
 * Algorithm:
 *   1. Sort server messages ASC by ts (chronological).
 *   2. Map to client shape (skip non-renderable rows).
 *   3. Walk `current` as the chronological spine:
 *      - Client-only ephemerals pass through unchanged.
 *      - Server-owned kinds matched to a server entry by from + dateLocal
 *        + text. Match → substitute with server version (gains the
 *        server ULID + canonical timestamp). No match → keep client
 *        version (in-flight, or pre-SSE-era entry).
 *   4. Append any server messages not consumed during the walk — in
 *      chronological order (because serverClient is ASC) — cross-
 *      device additions or messages from a session whose cache
 *      was cleared.
 *
 * Match is by content, not ID: client temp IDs (`u-${ts}`, `p-${ts}`)
 * never align with server ULIDs, so structural match is the only path.
 * After one reconciliation cycle, subsequent sessions persist server
 * IDs to cache and matching trivially aligns by ID-as-content-hash.
 *
 * `consumedServer` ensures we don't double-substitute when two cached
 * messages have identical content (user said "hi" twice). First cache
 * "hi" matches first server "hi" by index order; second matches second.
 * Order-preserving across same-content duplicates — and the ASC sort
 * in step 1 is what makes "first server entry" mean "earliest in time",
 * which is the only intuitive interpretation of the order-preserving
 * invariant.
 *
 * Reverse-render bug (16 Jun 2026):
 *
 *   The server's GET /v1/chat/history returns messages DESC by ts
 *   (cursor-pagination shape — newest-first lets a `before` cursor
 *   walk backward through history). Before this fix, `serverClient`
 *   was built directly from `server.messages` in receipt order
 *   (DESC). On the sign-out-then-sign-back-in rehydration path
 *   (clearAllAppStorage wiped localStorage on the outbound
 *   sign-out, so MePage's hydration produced `current = []`),
 *   every server message flowed through the append-unconsumed
 *   tail in step 4 — in DESC order. The merged array was newest
 *   first → render placed newest at the top of the scroll area and
 *   oldest directly above the chat input. MePage's writeChatHistory
 *   effect then persisted that reverse-ordered array back to
 *   localStorage, locking the bug in until the cache cleared.
 *
 *   The one-line sort below fixes both:
 *   (a) the rehydration path renders chronologically because the
 *       tail branch now appends in ASC order, and
 *   (b) the duplicate-content `findIndex` pairing matches first-
 *       chronological-to-first-chronological as the existing comment
 *       claimed but didn't actually enforce while serverClient was
 *       DESC.
 *
 *   Self-healing migration: clearAllAppStorage runs on sign-out
 *   (SettingsPage.handleSignOut, JoinPage.handleSignOut). One sign-
 *   out cycle clears any user's reverse-cached state, and the next
 *   sign-in rebuilds chronologically through this function. No
 *   forced cache invalidation needed.
 *
 *   `localeCompare` on ISO 8601 strings is correct: the format is
 *   fixed-width big-endian (YYYY-MM-DDTHH:MM:SS.sssZ), so
 *   lexicographic comparison aligns with chronological order to
 *   millisecond precision. Server's ts column is timestamptz →
 *   Date → JSON.stringify, which always emits ISO 8601.
 */
export function reconcileHistory(
  current: ReadonlyArray<ChatMessage>,
  server: HistoryResponse,
): ChatMessage[] {
  const serverAsc = [...server.messages].sort((a, b) =>
    a.ts.localeCompare(b.ts),
  );
  const serverClient = serverAsc
    .map(serverMessageToClient)
    .filter((m): m is ChatMessage => m !== null);

  const consumedServer = new Set<string>();
  const merged: ChatMessage[] = [];

  for (const cur of current) {
    if (!SERVER_OWNED_CLIENT_KINDS.has(cur.kind)) {
      merged.push(cur);
      continue;
    }

    const matchIdx = serverClient.findIndex(
      (sm) =>
        !consumedServer.has(sm.id) &&
        sm.from === cur.from &&
        sm.dateLocal === cur.dateLocal &&
        sm.text === cur.text,
    );
    if (matchIdx >= 0) {
      const matched = serverClient[matchIdx];
      if (matched) {
        consumedServer.add(matched.id);
        merged.push(matched);
        continue;
      }
    }
    // No match — keep client version. Either still in-flight (placeholder
    // with empty text won't match anyway), or a row from before server
    // history existed for this user.
    merged.push(cur);
  }

  for (const sm of serverClient) {
    if (!consumedServer.has(sm.id)) {
      merged.push(sm);
    }
  }

  return merged;
}
